import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { fetchPlayer, toggleWatchlist, fetchWatchlist, getUserToken } from "../lib/api";
import { FullChart } from "../components/Sparkline";
import { PlayerAvatar } from "../components/PlayerAvatar";
import { ArrowLeft, Star, TrendUp, TrendDown } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function PlayerDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [activeProp, setActiveProp] = useState(null);
  const [watched, setWatched] = useState(new Set());
  const token = getUserToken();

  useEffect(() => {
    fetchPlayer(id).then((d) => {
      setData(d);
      setActiveProp(d.props?.[0] || null);
    });
    fetchWatchlist(token).then((d) => setWatched(new Set(d.props.map((p) => p.id))));
  }, [id, token]);

  if (!data) {
    return <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 text-[#A1A1AA]" data-testid="player-loading">Loading player…</div>;
  }

  const { player, props } = data;

  const handleWatch = async (propId) => {
    const wasWatched = watched.has(propId);
    const res = await toggleWatchlist(token, propId);
    setWatched((prev) => {
      const n = new Set(prev);
      if (res.status === "added") n.add(propId);
      else n.delete(propId);
      return n;
    });
    toast(wasWatched ? "Removed from My Picks" : "Added to My Picks");
  };

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-8">
      <Link to="/" className="overline inline-flex items-center gap-1.5 hover:text-white mb-6" data-testid="back-link">
        <ArrowLeft size={14} weight="bold" /> Back to Board
      </Link>

      {/* Player header */}
      <div className="grid md:grid-cols-[auto_1fr_auto] gap-6 items-center border-b border-[#27272A] pb-8">
        <PlayerAvatar player={player} size={96} />
        <div>
          <div className="overline">{player.sport} • {player.position}</div>
          <h1 className="font-head text-5xl text-white tracking-tight mt-1">{player.name}</h1>
          <div className="text-[#A1A1AA] mt-2 text-sm">
            {player.team} {player.is_home ? "vs" : "@"} {player.opponent} • {new Date(player.game_time).toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
          </div>
        </div>
        <div className="text-right">
          <div className="overline">Active Props</div>
          <div className="font-head text-4xl text-white mt-1">{props.length}</div>
        </div>
      </div>

      {/* Layout */}
      <div className="grid lg:grid-cols-[320px_1fr] gap-8 mt-8">
        {/* Prop list */}
        <aside>
          <div className="overline mb-3">Tracked Markets</div>
          <ul className="space-y-2" data-testid="player-prop-list">
            {props.map((p) => {
              const isActive = activeProp?.id === p.id;
              const isOver = p.pick === "OVER";
              return (
                <li key={p.id}>
                  <button
                    onClick={() => setActiveProp(p)}
                    className={`w-full text-left p-4 border transition-all ${isActive ? "border-[#FF4500] bg-[#1a1a1a]" : "border-[#27272A] hover:border-white/20"}`}
                    data-testid={`player-prop-${p.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="font-head text-white">{p.stat_type}</div>
                      <span className={`text-xs px-2 py-0.5 uppercase font-head tracking-wider ${isOver ? "pick-over" : "pick-under"}`}>
                        {p.pick} {p.line}
                      </span>
                    </div>
                    <div className="flex items-center justify-between mt-2 text-xs text-[#A1A1AA]">
                      <span>L10: <span className="text-white font-data">{p.hit_count}/{p.hit_total}</span></span>
                      <span>Conf: <span className="text-white font-data">{p.confidence}%</span></span>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        </aside>

        {/* Detail */}
        {activeProp && (
          <section data-testid="player-prop-detail">
            <div className="flex items-start justify-between border-b border-[#27272A] pb-4 mb-6">
              <div>
                <div className="overline">{activeProp.tier}</div>
                <h2 className="font-head text-3xl text-white mt-1 uppercase">{activeProp.stat_type}</h2>
                <p className="text-[#A1A1AA] text-sm mt-2 max-w-lg">{activeProp.reasoning}</p>
              </div>
              <div className="flex flex-col items-end gap-2">
                <div className={`flex items-center gap-2 px-4 py-2 font-head text-xl uppercase ${activeProp.pick === "OVER" ? "pick-over" : "pick-under"}`}>
                  {activeProp.pick === "OVER" ? <TrendUp weight="bold" /> : <TrendDown weight="bold" />}
                  {activeProp.pick} {activeProp.line}
                </div>
                <button
                  onClick={() => handleWatch(activeProp.id)}
                  className="overline inline-flex items-center gap-1.5 hover:text-[#FACC15] transition-colors"
                  data-testid={`detail-watch-${activeProp.id}`}
                >
                  <Star size={14} weight={watched.has(activeProp.id) ? "fill" : "regular"} color={watched.has(activeProp.id) ? "#FACC15" : "currentColor"} />
                  {watched.has(activeProp.id) ? "Picked" : "Add Pick"}
                </button>
              </div>
            </div>

            {/* Numbers */}
            <div className="grid grid-cols-4 gap-px bg-[#27272A] border border-[#27272A] mb-6">
              <StatBig label="Confidence" value={`${activeProp.confidence}%`} accent="#FF4500" />
              <StatBig label="L10 Hit Rate" value={`${activeProp.hit_count}/${activeProp.hit_total}`} />
              <StatBig label="L5 Avg" value={activeProp.last_5_avg} />
              <StatBig label="Season Avg" value={activeProp.season_avg} />
            </div>

            {/* Chart */}
            <div className="border border-[#27272A] bg-[#121212] p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="overline">Last 10 Games vs Line {activeProp.line}</div>
                <div className="text-xs text-[#A1A1AA] flex items-center gap-3">
                  <span className="inline-flex items-center gap-1.5">
                    <span className="block w-3 h-0.5" style={{ background: activeProp.pick === "OVER" ? "#FF4500" : "#00E5FF" }} />
                    Performance
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <span className="block w-3 border-t border-dashed border-white/60" />
                    Line
                  </span>
                </div>
              </div>
              <FullChart data={activeProp.last_10} line={activeProp.line} pick={activeProp.pick} />
            </div>

            {/* Game log */}
            <div className="mt-6">
              <div className="overline mb-3">Game Log</div>
              <div className="border border-[#27272A]">
                <table className="w-full text-sm" data-testid="game-log-table">
                  <thead>
                    <tr className="bg-[#121212] border-b border-[#27272A]">
                      <th className="text-left p-3 overline">Date</th>
                      <th className="text-left p-3 overline">Matchup</th>
                      <th className="text-right p-3 overline">Stat</th>
                      <th className="text-right p-3 overline">vs Line</th>
                      <th className="text-center p-3 overline">Result</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...activeProp.last_10].reverse().map((g, i) => {
                      const isOver = g.value > activeProp.line;
                      return (
                        <tr key={`${g.date}-${g.opponent}-${i}`} className="border-b border-[#1a1a1a] last:border-b-0">
                          <td className="p-3 text-[#A1A1AA] font-data">{g.date}</td>
                          <td className="p-3 text-white">{g.opponent}</td>
                          <td className="p-3 text-right font-data text-white">{g.value}</td>
                          <td className="p-3 text-right font-data" style={{ color: isOver ? "#FF4500" : "#00E5FF" }}>
                            {isOver ? "OVER" : "UNDER"} ({(g.value - activeProp.line).toFixed(1)})
                          </td>
                          <td className="p-3 text-center text-[#A1A1AA]">{g.result}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

function StatBig({ label, value, accent }) {
  return (
    <div className="bg-[#0A0A0A] p-5">
      <div className="overline">{label}</div>
      <div className="font-data text-3xl mt-1" style={{ color: accent || "#fff" }}>{value}</div>
    </div>
  );
}
